/**
 * `web_loaded` and `game_ready` — WebBridge.md §3, the two boot beacons.
 *
 * ❓ PLACEHOLDER — not in the decomp. The app uses them to take its own splash
 * down and to time the first ad, so they are the port's answer to
 * `Bootstrapper.<Awake>d__10.MoveNext` finishing: the original hands control to
 * the gameplay scene, this build has to *say* so.
 *
 * Neither takes parameters and neither is answered (WebBridge.md §3: "chỉ để
 * app ghi nhận"), so there is no `callbackId` and nothing to await.
 *
 * The two are **not** interchangeable, and the split is the whole point:
 *
 *   `web_loaded`  the bundle is running — HTML, CSS and JS are up. The app may
 *                 stop worrying about a white screen, but there is no board yet.
 *   `game_ready`  a level is standing and the boot cover is gone. This is the
 *                 first instant a tap would do something.
 *
 * Collapsing them (sending both at one point) is the failure worth naming: the
 * app would either hold its splash over an already-playable board, or lift it
 * onto `#bootCover` — which is a solid camera frame and looks exactly like a
 * hang. `App.load` has a `finally` that drops the cover on a *failed* load too,
 * so `game_ready` hangs off that same moment rather than off a successful
 * build: a build that threw still leaves the player looking at the page, and an
 * app that never gets `game_ready` sits on its splash for ever.
 *
 * Both are latched. `App.load` runs again on every restart and every Continue,
 * and a boot beacon repeated per level is not a boot beacon.
 */

import { nativeBridge, type NativeBridge } from './bridge'

export class BootBeacons {
  private sentWebLoaded = false
  private sentGameReady = false

  constructor(private readonly bridge: NativeBridge = nativeBridge) {}

  /** The bundle is executing. Safe to call more than once; only the first goes. */
  webLoaded(): void {
    if (this.sentWebLoaded) return
    this.sentWebLoaded = true
    this.bridge.send('web_loaded')
  }

  /**
   * The first board is on screen.
   *
   * Implies `webLoaded` — a host that only ever saw `game_ready` (because the
   * first load was fast enough to beat the beacon) must not be left without the
   * earlier event, since the two are a sequence to anything measuring boot time.
   */
  gameReady(): void {
    this.webLoaded()
    if (this.sentGameReady) return
    this.sentGameReady = true
    this.bridge.send('game_ready')
  }
}

export const bootBeacons = new BootBeacons()
